/**
 * Biodiversity Dashboard Component
 * 
 * Enhanced biodiversity assessment using PRD FR-002 requirements:
 * - Shannon/Simpson diversity indices
 * - Monoculture risk classification
 * - Species tracking
 * - Actionable recommendations
 * 
 * Data Source: Real-time from /api/biodiversity/metrics or fallback to static data
 * 
 * UI: Modern, clean, and easy to understand with visual indicators
 */

import React, { useState, useMemo } from 'react';
import {
  FaTree, FaLeaf, FaExclamationTriangle, FaCheckCircle,
  FaInfoCircle, FaChartPie, FaSeedling,
  FaSync
} from 'react-icons/fa';
import {
  calculateCompositeBiodiversityScore,
  getBiodiversityRating,
  MONOCULTURE_RISK_LEVELS
} from '../services/biodiversityService';
import { BIODIVERSITY_DATA, REGION_CONFIG } from '../data/dashboardData';
import { useBiodiversityMetrics } from '../hooks/useRealTimeData';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import '../styles/sustainability.css';

// Fallback static data configuration
const fallbackRegionData = {
  speciesCounts: BIODIVERSITY_DATA.speciesCounts,
  habitatQualityScore: BIODIVERSITY_DATA.habitatQualityScore,
  farmingPracticesScore: BIODIVERSITY_DATA.farmingPracticesScore,
  conservationScore: BIODIVERSITY_DATA.conservationScore,
  cropData: BIODIVERSITY_DATA.cropDistribution
};

const BiodiversityDashboard = () => {
  const [selectedRegion, setSelectedRegion] = useState(REGION_CONFIG.default);
  const [activeTab, setActiveTab] = useState('overview');

  const currentRegion = useMemo(() => {
    return REGION_CONFIG.regions[selectedRegion] || REGION_CONFIG.regions[REGION_CONFIG.default];
  }, [selectedRegion]);

  const [lat, lon] = currentRegion.center || REGION_CONFIG.mapCenter;

  // Fetch real-time biodiversity data
  const {
    data: realTimeData,
    loading,
    refetch,
    lastUpdated
  } = useBiodiversityMetrics(lat, lon, 25);

  // Get crop distribution data for display
  const cropDistributionData = useMemo(() => {
    if (realTimeData?.crop_distribution) {
      return realTimeData.crop_distribution;
    }
    return fallbackRegionData.cropData || BIODIVERSITY_DATA.cropDistribution || [
      { crop: 'Corn', area: 850000 },
      { crop: 'Soybeans', area: 620000 },
      { crop: 'Wheat', area: 280000 },
      { crop: 'Hay', area: 420000 },
      { crop: 'Other', area: 150000 }
    ];
  }, [realTimeData]);

  // Calculate biodiversity metrics - use API data if available
  const biodiversityAssessment = useMemo(() => {
    if (realTimeData?.metrics) {
      return {
        compositeScore: Math.round((realTimeData.metrics.shannon_index / 3) * 100),
        rating: getBiodiversityRating(Math.round((realTimeData.metrics.shannon_index / 3) * 100)),
        indices: {
          shannonIndex: realTimeData.metrics.shannon_index || 0,
          simpsonIndex: realTimeData.metrics.simpson_index || 0,
          speciesRichness: realTimeData.metrics.species_richness || 0,
          evenness: realTimeData.metrics.species_evenness || 0
        },
        components: {
          habitatQuality: {
            score: realTimeData.habitat_assessment?.quality_score || 70,
            weight: 30
          },
          farmingPractices: {
            score: 75,
            weight: 20
          },
          conservation: {
            score: realTimeData.habitat_assessment?.connectivity_score || 65,
            weight: 10
          },
          speciesDiversity: {
            score: Math.round((((realTimeData.metrics.shannon_index / 4.5) * 100 * 0.5) + (realTimeData.metrics.simpson_index * 100 * 0.5)) * 10) / 10,
            weight: 40
          }
        },
        monocultureRisk: (() => {
          const riskLevel = MONOCULTURE_RISK_LEVELS[realTimeData.metrics.monoculture_risk?.toUpperCase()] ||
            MONOCULTURE_RISK_LEVELS.MODERATE;
          return {
            riskLevel,
            dominantCrop: realTimeData.metrics.dominant_crop || 'Unknown',
            concentration: realTimeData.metrics.concentration_percentage || 50,
            recommendations: realTimeData.habitat_assessment?.recommendations || []
          };
        })(),
        recommendations: realTimeData.habitat_assessment?.recommendations || [
          'Maintain hedgerows and field margins',
          'Plant native species to support pollinators',
          'Reduce chemical inputs in sensitive areas'
        ],
        dataSource: realTimeData.data_source || 'Real-time API'
      };
    }

    return {
      ...calculateCompositeBiodiversityScore(fallbackRegionData),
      dataSource: 'Static Data (API Offline)'
    };
  }, [realTimeData]);

  const {
    compositeScore,
    rating,
    indices,
    components,
    monocultureRisk: rawMonocultureRisk,
    recommendations
  } = biodiversityAssessment;

  // Ensure monocultureRisk has the correct structure with safe defaults
  const monocultureRisk = useMemo(() => {
    if (!rawMonocultureRisk) {
      return {
        riskLevel: MONOCULTURE_RISK_LEVELS.MODERATE,
        dominantCrop: 'Unknown',
        concentration: 50,
        recommendations: []
      };
    }

    // If it's just a risk level object, wrap it in the proper structure
    if (rawMonocultureRisk.level && !rawMonocultureRisk.riskLevel) {
      return {
        riskLevel: rawMonocultureRisk,
        dominantCrop: 'Unknown',
        concentration: 50,
        recommendations: []
      };
    }

    // Ensure riskLevel is always present
    if (!rawMonocultureRisk.riskLevel) {
      return {
        ...rawMonocultureRisk,
        riskLevel: MONOCULTURE_RISK_LEVELS.MODERATE
      };
    }

    return rawMonocultureRisk;
  }, [rawMonocultureRisk]);

  const getScoreColor = (score) => {
    if (score >= 80) return 'hsl(142 76% 36%)';
    if (score >= 60) return 'hsl(45 93% 47%)';
    return 'hsl(0 84.2% 60.2%)';
  };

  return (
    <TooltipProvider>
      <div className="biodiversity-dashboard" style={{ padding: '1.5rem', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header Section */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-primary/10">
                  <FaTree className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-3xl mb-2">Biodiversity Assessment</CardTitle>
                  <CardDescription className="text-base">
                    Monitor species diversity and ecosystem health using Shannon-Simpson indices
                  </CardDescription>
                  <div className="flex items-center gap-3 mt-3 text-sm">
                    <Badge variant={realTimeData ? "default" : "secondary"}>
                      {loading ? (
                        <>
                          <FaSync className="w-3 h-3 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <span className="mr-2">📡</span>
                          {realTimeData ? 'Live Data' : 'Demo Mode'}
                        </>
                      )}
                    </Badge>
                    {lastUpdated && (
                      <span className="text-muted-foreground">
                        Updated: {lastUpdated.toLocaleTimeString()}
                      </span>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={refetch}
                      disabled={loading}
                    >
                      <FaSync className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-foreground">Region</label>
                <select
                  value={selectedRegion}
                  onChange={(e) => setSelectedRegion(e.target.value)}
                  className="px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm"
                >
                  {Object.values(REGION_CONFIG.regions).map(region => (
                    <option key={region.id} value={region.id}>{region.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Main Score Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Overall Score Card */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg">Overall Score</CardTitle>
              <CardDescription>Biodiversity Health Index</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center justify-center py-6">
                <div className="relative w-32 h-32 mb-4">
                  <svg className="transform -rotate-90 w-32 h-32">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="hsl(var(--muted))"
                      strokeWidth="8"
                      fill="none"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke={rating.color}
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 56}`}
                      strokeDashoffset={`${2 * Math.PI * 56 * (1 - compositeScore / 100)}`}
                      className="transition-all duration-500"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-4xl font-bold" style={{ color: rating.color }}>
                      {compositeScore}
                    </span>
                    <span className="text-sm text-muted-foreground">/100</span>
                  </div>
                </div>
                <Badge
                  variant="outline"
                  className="text-base px-4 py-1.5"
                  style={{
                    borderColor: rating.color,
                    color: rating.color,
                    backgroundColor: rating.color + '15'
                  }}
                >
                  {rating.rating}
                </Badge>
                <p className="text-sm text-muted-foreground mt-2 text-center">
                  {rating.description}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Diversity Indices Grid */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FaChartPie className="w-5 h-5" />
                Diversity Indices
              </CardTitle>
              <CardDescription>Scientific metrics for ecosystem diversity</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {/* Shannon Index */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Shannon Index (H')</span>
                    <Tooltip>
                      <TooltipTrigger>
                        <FaInfoCircle className="w-4 h-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Higher values indicate greater species diversity and evenness</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="text-2xl font-bold">{indices.shannonIndex.toFixed(2)}</div>
                  <Progress value={(indices.shannonIndex / 4.5) * 100} className="h-2" />
                  <div className="text-xs text-muted-foreground">Scale: 0 to ~4.5</div>
                </div>

                {/* Simpson Index */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Simpson Index (1-D)</span>
                    <Tooltip>
                      <TooltipTrigger>
                        <FaInfoCircle className="w-4 h-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Probability that two random individuals are different species</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="text-2xl font-bold">{indices.simpsonIndex.toFixed(3)}</div>
                  <Progress value={indices.simpsonIndex * 100} className="h-2" />
                  <div className="text-xs text-muted-foreground">Scale: 0 to 1</div>
                </div>

                {/* Species Richness */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Species Richness</span>
                  </div>
                  <div className="text-2xl font-bold">{indices.speciesRichness}</div>
                  <Progress value={(indices.speciesRichness / 30) * 100} className="h-2" />
                  <div className="text-xs text-muted-foreground">Total species count</div>
                </div>

                {/* Evenness */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Evenness (J)</span>
                  </div>
                  <div className="text-2xl font-bold">{indices.evenness.toFixed(3)}</div>
                  <Progress value={indices.evenness * 100} className="h-2" />
                  <div className="text-xs text-muted-foreground">Scale: 0 to 1</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="monoculture">Monoculture Risk</TabsTrigger>
            <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Component Scores */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Score Components</CardTitle>
                <CardDescription>Breakdown of biodiversity assessment factors</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(components).map(([key, componentData]) => {
                    // Handle both object format {score, weight} and number format (for backward compatibility)
                    const scoreValue = typeof componentData === 'object' && componentData !== null
                      ? componentData.score
                      : componentData;
                    const weightValue = typeof componentData === 'object' && componentData !== null
                      ? componentData.weight
                      : null;

                    const displayName = key.replace(/([A-Z])/g, ' $1').trim();
                    const color = getScoreColor(scoreValue);
                    return (
                      <div key={key} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{displayName}</span>
                          <div className="flex items-center gap-2">
                            {weightValue && (
                              <span className="text-xs text-muted-foreground">({weightValue}%)</span>
                            )}
                            <span className="text-sm font-bold" style={{ color }}>
                              {scoreValue}/100
                            </span>
                          </div>
                        </div>
                        <Progress
                          value={scoreValue}
                          className="h-3"
                          style={{
                            '--progress-background': color
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Crop Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FaSeedling className="w-5 h-5" />
                  Crop Distribution
                </CardTitle>
                <CardDescription>Land use distribution across the region</CardDescription>
              </CardHeader>
              <CardContent>
                {cropDistributionData && cropDistributionData.length > 0 ? (
                  <div className="space-y-4">
                    {cropDistributionData.map((crop, idx) => {
                      const totalArea = cropDistributionData.reduce((sum, c) => sum + (c.area || 0), 0);
                      const percentage = totalArea > 0 ? ((crop.area || 0) / totalArea * 100) : 0;
                      const cropName = crop.crop || crop.crop_name || 'Unknown';
                      return (
                        <div key={idx} className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium">{cropName}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-muted-foreground">
                                {(crop.area || 0).toLocaleString()} acres
                              </span>
                              <span className="font-bold">{percentage.toFixed(1)}%</span>
                            </div>
                          </div>
                          <Progress value={percentage} className="h-4" />
                        </div>
                      );
                    })}
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">Total Cropland</span>
                        <span className="font-bold">
                          {cropDistributionData.reduce((sum, c) => sum + (c.area || 0), 0).toLocaleString()} acres
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No crop distribution data available
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Monoculture Risk Tab */}
          <TabsContent value="monoculture" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  {monocultureRisk.riskLevel.level === 'Critical' || monocultureRisk.riskLevel.level === 'High'
                    ? <FaExclamationTriangle className="w-5 h-5 text-destructive" />
                    : <FaCheckCircle className="w-5 h-5 text-primary" />
                  }
                  Monoculture Risk Assessment
                </CardTitle>
                <CardDescription>Evaluation of crop diversity and concentration risk</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Risk Level Badge */}
                <div
                  className="p-6 rounded-lg border-2"
                  style={{
                    borderColor: monocultureRisk.riskLevel.color,
                    backgroundColor: monocultureRisk.riskLevel.color + '10'
                  }}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <Badge
                      className="text-lg px-4 py-2"
                      style={{
                        backgroundColor: monocultureRisk.riskLevel.color,
                        color: 'white'
                      }}
                    >
                      {monocultureRisk.riskLevel.level} Risk
                    </Badge>
                    <div className="flex-1">
                      <div className="text-sm text-muted-foreground mb-1">Dominant Crop Concentration</div>
                      <div className="text-2xl font-bold">
                        {monocultureRisk.concentration}% - {monocultureRisk.dominantCrop}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm">{monocultureRisk.riskLevel.description}</p>
                </div>

                {/* Risk Levels Legend */}
                <div>
                  <h4 className="text-sm font-semibold mb-3">Risk Classification Guide</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.values(MONOCULTURE_RISK_LEVELS).map((level) => (
                      <div
                        key={level.level}
                        className="flex items-center gap-3 p-3 rounded-md border"
                        style={{
                          borderColor: level.color + '40',
                          backgroundColor: level === monocultureRisk.riskLevel ? level.color + '10' : 'transparent'
                        }}
                      >
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: level.color }}
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium">{level.level}</div>
                          <div className="text-xs text-muted-foreground">{level.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recommendations Tab */}
          <TabsContent value="recommendations" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FaLeaf className="w-5 h-5" />
                  Action Recommendations
                </CardTitle>
                <CardDescription>Actionable steps to improve biodiversity</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recommendations.map((rec, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-3 p-4 rounded-lg border bg-card"
                    >
                      <div className="mt-0.5">
                        <FaCheckCircle className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{rec}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </TooltipProvider>
  );
};

export default BiodiversityDashboard;
